import React, { useCallback } from "react";
import { toast } from "react-toastify";
import {
  useActiveTab,
  useSubFormApproval,
  useProductDetailsApproval,
  useManufacturerSiteApproval
} from "../../../../../hooks";
import ApproveButton from "../../ApproveButton/ApproveButton";
import ActionButtons from "../../ActionButtons/ActionButtons";
import { FormSectionsLayout } from "./SharedComponents";
import { ROLES } from "../../../../../constants/roles";
import { STATUSES } from "../../../../../constants/statuses";
import axiosInstance from "../../../../../axiosInstance";

const rolesValidated = [{
  role: ROLES.business_validator,
  status: STATUSES.ROLE_STATUSES[ROLES.business_validator].ACTIVE
}, {
  role: ROLES.core_Validator,
  status: STATUSES.ROLE_STATUSES[ROLES.core_Validator].ACTIVE
}, {
  role: ROLES.RA,
  status: STATUSES.ROLE_STATUSES[ROLES.RA].ACTIVE
}];

export const FormSectionsNonCustomer = ({
  subForms,
  requestProgressId,
  prevComments,
  requestDetails,
  filledForm,
  requestId,
  handleAddComment,
  requestProgress,
  children,
  handleAction,
  roleName = '',
  formName = null,
  hasProductDetails = false,
  hasManufacturersSite = false,
  isSendbackMode = false,
  productDetailsData = null,
}) => {
  // state to hold product details and manufacturer site comments
  const [productDetailsComments, setProductDetailsComments] = React.useState([]);
  const [manufacturerSiteComments, setManufacturerSiteComments] = React.useState([]);
  const [commentsRefetchKey, setCommentsRefetchKey] = React.useState(0);

  // fetch product details approval status
  const {
    isApproved: isProductDetailsApproved,
    approveProductDetails,
    deleteApproval: deleteProductDetailsApproval,
  } = useProductDetailsApproval({
    productDetailsId: productDetailsData?.id,
    requestId: requestId,
    persona: null
  });

  // fetch manufacturer site approval status
  const {
    isApproved: isManufacturerSiteApproved,
    approveManufacturerSite,
    deleteApproval: deleteManufacturerSiteApproval,
  } = useManufacturerSiteApproval({
    manufacturerSiteId: requestDetails?.manufacturersSite?.id,
    requestId: requestId,
    persona: null
  });

  // fetch product details comments
  React.useEffect(() => {
    const fetchProductDetailsComments = async () => {
      if (!productDetailsData?.id) {
        setProductDetailsComments([]);
        return;
      }
      try {
        const response = await axiosInstance.get(`/api/product-details-comments/${productDetailsData.id}`);
        setProductDetailsComments(response.data || []);
      } catch (error) {
        console.error('error fetching product details comments:', error);
        setProductDetailsComments([]);
      }
    };

    fetchProductDetailsComments();
  }, [productDetailsData?.id, commentsRefetchKey]);

  // fetch manufacturer site comments
  React.useEffect(() => {
    const fetchManufacturerSiteComments = async () => {
      if (!requestDetails?.manufacturersSite?.id) {
        setManufacturerSiteComments([]);
        return;
      }
      try {
        const response = await axiosInstance.get(`/api/manufacturers-site-comments/${requestDetails.manufacturersSite.id}`);
        setManufacturerSiteComments(response.data || []);
      } catch (error) {
        console.error('error fetching manufacturer site comments:', error);
        setManufacturerSiteComments([]);
      }
    };

    fetchManufacturerSiteComments();
  }, [requestDetails?.manufacturersSite?.id, commentsRefetchKey]);

  // callback to refetch comments after adding a new one
  const handleAddCommentWrapper = useCallback(async (...args) => {
    // call the original handleAddComment
    if (handleAddComment) {
      await handleAddComment(...args);
    }
    // trigger refetch of comments to update icon
    setCommentsRefetchKey(prev => prev + 1);
  }, [handleAddComment]);

  // create custom subforms for product details and manufacturers site
  const customSubForms = React.useMemo(() => {
    const customs = [];

    if (hasProductDetails) {
      customs.push({
        id: 'custom_product_details',
        name: 'Product Details',
        isCustom: true,
        approved: isProductDetailsApproved ? 1 : 0,
      });
    }
    if (hasManufacturersSite) {
      customs.push({
        id: 'custom_manufacturers_site',
        name: 'Manufacturers Site',
        isCustom: true,
        approved: isManufacturerSiteApproved ? 1 : 0,
      });
    }
    return customs;
  }, [hasProductDetails, hasManufacturersSite, isProductDetailsApproved, isManufacturerSiteApproved]);

  // merge subForms with custom subforms
  const allSubForms = React.useMemo(() => {
    return [...subForms, ...customSubForms];
  }, [subForms, customSubForms]);

  const [activeTab, setActiveTab] = useActiveTab(allSubForms);
  
  // Use the hook for both fetching and actions (only for regular subforms)
  const { 
    subForms: subFormsWithApproval,
    isCheckingApprovals,
    isApproving,
    isDeleting,
    approveSubForm,
    deleteApproval,
  } = useSubFormApproval({
    enduserFilledFormId: filledForm?.id,
    subForms: subForms, // only pass regular subforms, not custom ones
    persona: null
  });

  // count comments for product details and manufacturer site
  const productDetailsCommentsCount = productDetailsComments.length;
  const manufacturerSiteCommentsCount = manufacturerSiteComments.length;

  // merge subforms with custom forms and their approval status
  const allFormsWithApproval = React.useMemo(() => {
    const customFormsWithStatus = customSubForms.map(customForm => ({
      ...customForm,
      // set approval based on the specific hook for each custom form
      approved: customForm.id === 'custom_product_details'
        ? (isProductDetailsApproved ? 1 : 0)
        : customForm.id === 'custom_manufacturers_site'
          ? (isManufacturerSiteApproved ? 1 : 0)
          : 0,
      // add comment count for custom forms
      hasComments: customForm.id === 'custom_product_details'
        ? productDetailsCommentsCount > 0
        : customForm.id === 'custom_manufacturers_site'
          ? manufacturerSiteCommentsCount > 0
          : false
    }));

    return [
      ...subFormsWithApproval,
      ...customFormsWithStatus
    ];
  }, [subFormsWithApproval, customSubForms, isProductDetailsApproved, isManufacturerSiteApproved, productDetailsCommentsCount, manufacturerSiteCommentsCount]);

  // Get the current tab's approval status from the updated forms
  const currentSubForm = allFormsWithApproval?.find(form => form.id === activeTab);
  const isCurrentTabApproved = !!currentSubForm?.approved;

  // Handle approve action
  const handleApprove = useCallback(async () => {
    if (!activeTab) return;

    let success = false;

    // Check if it's a custom form
    if (activeTab === 'custom_product_details') {
      success = await approveProductDetails();
    } else if (activeTab === 'custom_manufacturers_site') {
      success = await approveManufacturerSite();
    } else {
      // Regular subform
      if (!filledForm?.id) return;
      success = await approveSubForm(activeTab);
    }

    if (success) {
      toast.success("Section approved successfully!", {
        position: "top-center",
      });
    } else {
      toast.error("Failed to approve section", {
        position: "top-center",
      });
    }
  }, [activeTab, filledForm?.id, approveSubForm, approveProductDetails, approveManufacturerSite]);

  // Handle undo action
  const handleUndo = useCallback(async () => {
    if (!activeTab) return;

    let success = false;

    // Check if it's a custom form
    if (activeTab === 'custom_product_details') {
      success = await deleteProductDetailsApproval();
    } else if (activeTab === 'custom_manufacturers_site') {
      success = await deleteManufacturerSiteApproval();
    } else {
      // Regular subform
      if (!filledForm?.id) return;
      success = await deleteApproval(activeTab);
    }

    if (success) {
      toast.success("Section approval removed successfully!", {
        position: "top-center",
      });
    } else {
      toast.error("Failed to remove approval", {
        position: "top-center",
      });
    }
  }, [activeTab, filledForm?.id, deleteApproval, deleteProductDetailsApproval, deleteManufacturerSiteApproval]);

  return (
    <FormSectionsLayout
      forms={allFormsWithApproval}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      prevComments={prevComments}
      requestDetails={requestDetails}
      requestProgressId={requestProgressId}
      persona={null}
      filledForm={filledForm}
      requestId={requestId}
      handleAddComment={handleAddCommentWrapper}
      enableAddComment={
        rolesValidated.some(
          (r) => r.role === roleName && r.status === requestProgress?.status
        )
      }
      requestProgress={requestProgress}
      // Pass approval-related props
      formsWithApprovalStatus={allFormsWithApproval}
      isCheckingApprovals={isCheckingApprovals}
      skipApprovalFetch={true}
      hasProductDetails={hasProductDetails}
      hasManufacturersSite={hasManufacturersSite}
      isSendbackMode={isSendbackMode}
      productDetailsData={productDetailsData}
      roleName={roleName}
      // Pass approval handlers and state for custom forms
      customFormApprovalProps={{
        isApproved: isCurrentTabApproved,
        isApproving: isApproving,
        isDeleting: isDeleting,
        onApprove: rolesValidated.some((r) => r.role === roleName && r.status === requestProgress?.status) ? handleApprove : null,
        onUndo: rolesValidated.some((r) => r.role === roleName && r.status === requestProgress?.status) ? handleUndo : null,
        onCommentAdded: () => setCommentsRefetchKey(prev => prev + 1),
      }}
      subformActionButtons={
      rolesValidated.some(
        (r) => r.role === roleName && r.status === requestProgress?.status
      ) &&
        <ApproveButton
          onApprove={handleApprove}
          onUndo={handleUndo}
          isApproving={isApproving}
          isDeleting={isDeleting}
          isApproved={isCurrentTabApproved}
          requestProgress={requestProgress}
          children={children}
        />
      }
      formActionButtons={
        roleName === ROLES.business_validator &&
        requestProgress?.status === STATUSES.ROLE_STATUSES[ROLES.business_validator].ACTIVE &&
        (
          <ActionButtons
            requestProgress={requestProgress}
            children={children}
            handleAction={handleAction}
            requestProgressId={requestProgressId}
            prevComments={prevComments}
          />
        )
      }
    />
  );
};
